import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { ScareLogo } from "../components/Brand";
import { GoogleButton, PasswordStrengthBar, passwordStrength } from "../components/AuthBits";
import { Eye, EyeOff, MailCheck } from "lucide-react";

export default function SignupPage() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // { email, verification_url }

  const valid = passwordStrength(form.password).valid;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setErr("Please choose a stronger password."); return; }
    setErr(""); setLoading(true);
    try {
      const r = await register(form.email, form.password, form.name);
      setSent({ email: r.email, verification_url: r.verification_url });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md st-card p-10 st-fade-up text-center" data-testid="signup-check-email">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#160a0a] items-center justify-center text-[#dc2626] mb-5"><MailCheck size={32} /></div>
          <h1 className="text-2xl font-bold mb-2">Check your email</h1>
          <p className="text-[#bbb]">We've sent a verification link to <span className="text-white">{sent.email}</span>. Click it to activate your account.</p>
          {sent.verification_url && (
            <div className="mt-5 text-left bg-[#0e0e0e] border border-[#1a1a1a] rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-1">Dev mode — email is mocked</div>
              <Link to={sent.verification_url.replace(window.location.origin, "")} className="st-link text-sm break-all" data-testid="signup-verify-link">Click here to verify now →</Link>
            </div>
          )}
          <p className="text-sm text-[#8a8a8a] mt-6"><Link to="/login" className="st-link" data-testid="goto-login">Back to sign in</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md st-card p-10 st-fade-up">
        <div className="flex justify-center mb-6"><ScareLogo size={36} /></div>
        <h1 className="text-2xl font-bold mb-1">Start selling tickets for your scare attraction</h1>
        <p className="text-[#8a8a8a] mb-6">Free to join. No monthly fees. Pay only when you sell.</p>

        <GoogleButton label="Continue with Google" />
        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 st-divider" />
          <span className="text-xs text-[#7a7a7a] uppercase tracking-wider">or</span>
          <div className="flex-1 st-divider" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <div>
            <label className="text-sm text-[#bbb]">Full name</label>
            <input data-testid="signup-name" className="st-input mt-1" required
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-sm text-[#bbb]">Email address</label>
            <input data-testid="signup-email" className="st-input mt-1" type="email" required
                   value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="text-sm text-[#bbb]">Password</label>
            <div className="relative">
              <input data-testid="signup-password" className="st-input mt-1 pr-10" type={show ? "text" : "password"} required
                     value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Create a password" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3 text-[#8a8a8a] hover:text-white" data-testid="signup-toggle-password" aria-label="Toggle password">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <PasswordStrengthBar value={form.password} />
          </div>
          {err && <div data-testid="signup-error" className="text-sm text-[#fca5a5] bg-[#2a1212] border border-[#5b1919] rounded-lg p-3">{err}</div>}
          <button data-testid="signup-submit" disabled={loading} className="st-btn w-full justify-center">
            {loading ? "Creating account…" : "Create Account"}
          </button>
        </form>

        <p className="text-center text-sm text-[#8a8a8a] mt-6">
          Already have an account? <Link to="/login" className="st-link" data-testid="goto-login">Sign in</Link>
        </p>
        <p className="text-center text-[11px] text-[#6a6a6a] mt-3">By signing up you agree to our Terms of Service and Privacy Policy.</p>
      </div>
    </div>
  );
}
